import { createContext, useContext, useCallback, useReducer } from 'react'

import {
  cleanPersistedFilters,
  useInitQueryString,
  useInitSearchSession,
  useSearchPersistence,
} from './searchPersistence'

import { usePaginatedQuery } from './paginatedQuery'

const PaginatedListContext = createContext()
const PaginatedListDispatchContext = createContext()

const SET_FILTERS = 'set filters'
const SET_SUB_FILTERS = 'set sub filters'
const SET_SORT = 'set sort'
const SET_PREFERENCES = 'set preferences'
const SET_FILTERS_AND_SORT = 'set filters and sort'
const SET_OPEN_FACETS_DRAWER = 'set open facets drawer'

function computeFilters({ current, payload }) {
  const newFilters = { ...current, ...payload }

  for (const name in newFilters) {
    if (newFilters[name] === undefined) {
      delete newFilters[name]
    }
  }
  return newFilters
}

/**
 *
 * const { field, entity, parentValue, values = {} } = payload
 *
 * field: the field in the filters related (eg. offerSubFilters, categorySubFilters)
 * entity: the main entity to be filtered (eg. offerType, categoryId)
 * parentValue: the value of the main entity to which the subFilter is related
 * values: object with subFilter values (eg.
 *  { offersProcessStatuses : [
 *        "inEdition",
 *        "onHold",
 *        "pending",
 *        "pendingDelivery",
 *        "rejected"
 *    ])
 *  }
 *
 */
function computeSubFilters({ current, payload }) {
  const { field, entity, parentValue, values = {} } = payload

  const subFilters = [...(current[field] || [])]
  const filterIndex = subFilters.findIndex(sub => sub[entity] === parentValue)

  if (filterIndex > -1) {
    // Update existing subfilter
    subFilters[filterIndex] = {
      ...subFilters[filterIndex],
      ...values,
    }

    // Remove undefined fields
    for (const key in subFilters[filterIndex]) {
      if (subFilters[filterIndex][key] === undefined) {
        delete subFilters[filterIndex][key]
      }
    }
  } else {
    //Adds a new subfilter if none exists
    subFilters.push({ [entity]: parentValue, ...values })
  }

  return {
    ...current,
    [field]: subFilters,
  }
}

/**
 * Filters & sort coming from the query string take precedence over session and initial values
 * Preferences coming from session take precedence over initial values
 */
function init({ initialFilters, initialSort, initialPreferences, session, queryString, ignorePersistedFilters }) {
  const { filters: filtersSession, sort: sortSession, preferences: preferencesSession } = session
  const { filters: filtersQs, sort: sortQs } = queryString

  const persistedFilters = cleanPersistedFilters({
    filters: { ...filtersSession, ...filtersQs },
    ignorePersistedFilters,
  })

  return {
    filters: { ...initialFilters, ...persistedFilters },
    sort: sortQs ?? sortSession ?? initialSort,
    preferences: { ...initialPreferences, ...preferencesSession },
  }
}

function reducer(state, action) {
  const { type, payload } = action

  switch (type) {
    case SET_FILTERS: {
      return { ...state, filters: computeFilters({ current: state.filters, payload }) }
    }
    case SET_SUB_FILTERS: {
      return { ...state, filters: computeSubFilters({ current: state.filters, payload }) }
    }
    case SET_SORT: {
      return { ...state, sort: payload }
    }
    case SET_PREFERENCES: {
      return { ...state, preferences: { ...state.preferences, ...payload } }
    }
    case SET_FILTERS_AND_SORT: {
      return {
        ...state,
        filters: computeFilters({ current: state.filters, payload: payload.filters }),
        sort: payload.sort ?? state.sort,
      }
    }
    case SET_OPEN_FACETS_DRAWER: {
      return { ...state, openFacetsDrawer: payload }
    }
    default:
      throw new Error()
  }
}

function PaginatedListProvider({
  query,
  initialFilters,
  initialSort,
  initialPreferences,
  pagingConfig,
  options,
  sessionKey: forceSessionKey,
  ignoreSession = false,
  ignorePersistedFilters,
  ignoreQueryStringPersistence = true,
  formatOutboundFilter,
  children,
}) {
  const queryString = useInitQueryString({ ignoreQueryStringPersistence })
  const { sessionKey, session } = useInitSearchSession({ ignoreSession, forceSessionKey })

  const [state, dispatch] = useReducer(
    reducer,
    init({
      initialFilters,
      initialSort,
      initialPreferences,
      session,
      queryString,
      ignorePersistedFilters,
    }),
  )

  useSearchPersistence({ state, sessionKey, ignoreQueryStringPersistence })

  const {
    items,
    paging,
    facets,
    loading,
    nextLimit,
    error,
    fetchMore,
    refetch,
    networkStatus,
    refetching,
    updateQuery,
    startPolling,
    stopPolling,
  } = usePaginatedQuery({
    query,
    filters: state.filters,
    sort: state.sort,
    pagingConfig,
    options,
    formatOutboundFilter,
  })

  return (
    <PaginatedListContext.Provider
      value={{
        ...state,
        items,
        paging,
        facets,
        loading,
        nextLimit,
        error,
        fetchMore,
        refetch,
        networkStatus,
        refetching,
        updateQuery,
        startPolling,
        stopPolling,
        skip: options?.skip,
      }}
    >
      <PaginatedListDispatchContext.Provider value={dispatch}>{children}</PaginatedListDispatchContext.Provider>
    </PaginatedListContext.Provider>
  )
}

function usePaginatedListState() {
  const {
    filters,
    sort,
    preferences,
    items,
    paging,
    facets,
    loading,
    nextLimit,
    error,
    fetchMore,
    refetch,
    networkStatus,
    refetching,
    updateQuery,
    startPolling,
    stopPolling,
    skip,
    openFacetsDrawer,
  } = useContext(PaginatedListContext)
  return {
    filters,
    sort,
    preferences,
    items,
    paging,
    facets,
    loading,
    nextLimit,
    error,
    fetchMore,
    refetch,
    networkStatus,
    refetching,
    updateQuery,
    startPolling,
    stopPolling,
    skip,
    openFacetsDrawer,
  }
}

function usePaginatedListDispatch() {
  const dispatch = useContext(PaginatedListDispatchContext)

  const setFilters = useCallback(
    newFilters => {
      dispatch({ type: SET_FILTERS, payload: newFilters })
    },
    [dispatch],
  )

  const setSubFilters = useCallback(
    newFilters => {
      dispatch({ type: SET_SUB_FILTERS, payload: newFilters })
    },
    [dispatch],
  )

  const setSort = useCallback(
    value => {
      dispatch({ type: SET_SORT, payload: value })
    },
    [dispatch],
  )
  const setPreferences = useCallback(
    value => {
      dispatch({ type: SET_PREFERENCES, payload: value })
    },
    [dispatch],
  )

  const setFiltersAndSort = useCallback(
    payload => {
      dispatch({ type: SET_FILTERS_AND_SORT, payload })
    },
    [dispatch],
  )

  const setOpenFacetsDrawer = useCallback(
    payload => {
      dispatch({ type: SET_OPEN_FACETS_DRAWER, payload })
    },
    [dispatch],
  )

  return { setFilters, setSort, setPreferences, setFiltersAndSort, setOpenFacetsDrawer, setSubFilters }
}

/**
 * Usage should be restricted to components/Commons/PaginatedList/List
 */
function usePaginatedListQuery() {
  const { items, paging, loading, nextLimit, error, fetchMore, refetch, networkStatus, skip } = usePaginatedListState()
  return { items, paging, loading, nextLimit, error, fetchMore, refetch, networkStatus, skip }
}

/**
 * Read-only of items fetched
 */
function usePaginatedListItems() {
  const { items, paging, facets } = useContext(PaginatedListContext) ?? {}
  return { items, paging, facets }
}

/**
 * Loads the next page. Encapsulates the skip/limit cursor and the hasNext/loading guard
 */
function usePaginatedListLoadMore() {
  const { items, paging, loading, nextLimit, fetchMore } = usePaginatedListState()
  const loadedCount = items?.length ?? 0

  return useCallback(() => {
    if (paging?.hasNext && !loading) {
      fetchMore({ variables: { paging: { skip: loadedCount, limit: nextLimit } } })
    }
  }, [loadedCount, paging?.hasNext, loading, nextLimit, fetchMore])
}

function usePaginatedList() {
  const {
    filters,
    sort,
    preferences,
    refetch,
    loading,
    refetching,
    updateQuery,
    startPolling,
    stopPolling,
    openFacetsDrawer,
  } = usePaginatedListState()

  const { setFilters, setSort, setPreferences, setFiltersAndSort, setOpenFacetsDrawer, setSubFilters } =
    usePaginatedListDispatch()

  return {
    filters,
    setFilters,
    sort,
    setSort,
    openFacetsDrawer,
    setOpenFacetsDrawer,
    preferences,
    setPreferences,
    setFiltersAndSort,
    refetch,
    loading,
    refetching,
    updateQuery,
    startPolling,
    stopPolling,
    setSubFilters,
  }
}

export {
  PaginatedListProvider,
  usePaginatedList,
  usePaginatedListItems,
  usePaginatedListLoadMore,
  usePaginatedListQuery,
}
